import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { STORE_KEYS, useAppSelector } from 'redux/store';
import {
  clearAllCheckpointFilters,
  getPageKeyFromRoute,
  ICheckpointFilters,
  initializeCheckpointFilters,
  updateCheckpointFilters,
} from 'helpers/checkpointFilters';

/**
 * Custom hook for managing checkpoint filters with URL and localStorage persistence
 */
export const useCheckpointFilters = () => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAppSelector(STORE_KEYS.USER);

  /**
   * Initialize filters on component mount
   */
  const initializeFilters = useCallback(() => {
    return initializeCheckpointFilters(
      location.pathname,
      location.search,
      history,
      1, // default page
      user?.paginationPreference, // default limit from user preferences
    );
  }, [location.pathname, location.search, history, user?.paginationPreference]);

  /**
   * Update filters and persist to both URL and localStorage
   */
  const updateFilters = useCallback(
    (newFilters: Partial<ICheckpointFilters>) => {
      updateCheckpointFilters(location.pathname, location.search, newFilters, history);
    },
    [location.pathname, location.search, history],
  );

  /**
   * Get the current page key for the page
   */
  const getPageKey = useCallback(() => {
    return getPageKeyFromRoute(location.pathname);
  }, [location.pathname]);

  /**
   * Check if current page supports filters
   */
  const isFilterPage = useCallback(() => {
    return getPageKey() !== null;
  }, [getPageKey]);

  /**
   * Clear all checkpoint filters from localStorage
   */
  const clearAllFilters = useCallback(() => {
    clearAllCheckpointFilters();
  }, []);

  return {
    initializeFilters,
    updateFilters,
    getPageKey,
    isFilterPage,
    clearAllFilters,
  };
};

export default useCheckpointFilters;
